// src/estimateentry.js
import { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import InvoiceTaxPanel from './InvoiceTaxPanel';
import AddressAutocomplete from './AddressAutocomplete';

/* ===============================
   HELPERS (mirrors invoiceentry.js patterns)
================================== */
const safeUUID = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const roundCurrency = (n) => Number(Number(n || 0).toFixed(2));
const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});


const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

// ✅ Inventory name (DB uses partName)
const getInvName = (item) => {
  const candidates = [item?.partName, item?.name, item?.part_name, item?.title];
  for (const v of candidates) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return 'Inventory Item';
};

const getInvDesc = (item) => {
  const candidates = [item?.description, item?.desc, item?.details, item?.notes, item?.note];
  for (const v of candidates) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

// ✅ Inventory qty (DB uses qtyOnHand)
const getInvQty = (item) => {
  const v =
    item?.qty_on_hand ??
    item?.qtyOnHand ??
    item?.on_hand ??
    item?.onHand ??
    item?.quantity ??
    item?.qty ??
    null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const hasInvQtyField = (item) => {
  if (!item) return false;
  return (
    ('qty_on_hand' in item) ||
    ('qtyOnHand' in item) ||
    ('on_hand' in item) ||
    ('onHand' in item) ||
    ('quantity' in item) ||
    ('qty' in item)
  );
};

// ✅ Retail calc for your DB inventory: cost + markup => retail
const getRetail = (item) => {
  const directCandidates = [
    item?.retail,
    item?.retail_price,
    item?.retailPrice,
    item?.sale_price,
    item?.salePrice,
    item?.sell_price,
    item?.sellPrice,
    item?.price,
    item?.rate
  ];
  for (const v of directCandidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }

  const cost = Number(item?.cost);
  const markup = Number(item?.markup);
  if (Number.isFinite(cost) && Number.isFinite(markup)) {
    const retail = cost * (1 + (markup / 100));
    if (Number.isFinite(retail)) return retail;
  }

  return 0;
};

/**
 * ✅ TAX LOGIC (kept)
 * - Each line item can be taxable or not: item.taxable === true/false
 * - Notes are never taxable
 * - Discount reduces taxable base proportionally (simple + fair)
 */
const computeTotals = (est) => {
  const items = Array.isArray(est.items) ? est.items : [];
  const billable = items.filter(i => i.type !== 'note');

  const subtotal = billable.reduce((acc, i) => {
    const qty = Number(i.qty || 1);
    const rate = Number(i.rate || 0);
    return acc + qty * rate;
  }, 0);

  const discountStr = String(est.discount || "0");
  const [discType, discVal] = discountStr.includes(":") ? discountStr.split(":") : ["fixed", discountStr];
  const discountValue = Number(discVal || 0);
  const rawDiscount = discType === "percentage" ? (subtotal * (discountValue / 100)) : discountValue;
  const discount = Math.min(Math.max(rawDiscount, 0), subtotal);
  const afterDisc = subtotal - discount;

  const taxableSubtotal = billable.reduce((acc, i) => {
    const isTaxable = i.taxable === true;
    if (!isTaxable) return acc;
    const qty = Number(i.qty || 1);
    const rate = Number(i.rate || 0);
    return acc + qty * rate;
  }, 0);

  let taxableAfterDisc = taxableSubtotal;
  if (subtotal > 0 && discount > 0) {
    const ratio = Math.max(0, Math.min(1, taxableSubtotal / subtotal));
    taxableAfterDisc = Math.max(0, taxableSubtotal - (discount * ratio));
  }

  const stateTax = Number(est.sales_tax_rate || 0);
  const localTax = Number(est.county_sales_tax_rate || 0);
  const stateTaxAmount = taxableAfterDisc * (stateTax / 100);
  const countyTaxAmount = taxableAfterDisc * (localTax / 100);
  const totalTaxAmount = stateTaxAmount + countyTaxAmount;

  const total = afterDisc + totalTaxAmount;
  return { subtotal, total, stateTaxAmount, countyTaxAmount };
};

const buildAddressLine = (c) => {
  const street = (c?.street || '').trim();
  const city = (c?.city || '').trim();
  const st = (c?.state || c?.st || '').trim();
  const zip = (c?.zip || '').trim();

  const line1 = street;
  const line2 = [city, st, zip].filter(Boolean).join(' ');
  const full = [line1, line2].filter(Boolean).join(', ');
  return full;
};

/* ===============================
   PHONE FORMATTER
================================== */
const formatPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/* ===============================
   QUICK ADD CUSTOMER MODAL
   ✅ ONLY CHANGES REQUESTED:
   - COMPANY -> CUSTOMER NAME / COMPANY
   - CONTACT -> CONTACT (OPTIONAL)
================================== */
const QuickAddCustomerModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    company: '',
    contact: '',
    email: '',
    office: '',
    mobile: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    terms: 'COD'
  });

  const styles = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px' },
    modal: { background: '#fff', border: '10px solid black', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' },
    header: { padding: '14px', borderBottom: '6px solid black', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 900 },
    body: { padding: '14px' },
    input: { padding: '12px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
    rowAddr: { display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.6fr 1fr', gap: '10px' },
    btnRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' },
    saveBtn: { backgroundColor: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    cancelBtn: { backgroundColor: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = () => {
    if (!form.company.trim() && !form.contact.trim()) {
      alert('Please enter at least a CUSTOMER NAME/COMPANY or CONTACT.');
      return;
    }
    const customer = { ...form, id: Date.now(), revenue: 0.0 };
    onSave(customer);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <style>{`
        .jf-hover-blue { transition: filter 120ms ease, transform 120ms ease; }
        .jf-hover-blue:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          filter: brightness(1.02);
        }
        .jf-hover-blue:active { transform: translateY(1px); }
      `}</style>

      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>QUICK ADD CUSTOMER</div>
          <button className="jf-hover-blue" style={styles.cancelBtn} onClick={onClose}>CLOSE</button>
        </div>

        <div style={styles.body}>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={styles.row2}>
              <input
                style={styles.input}
                placeholder="CUSTOMER NAME / COMPANY"
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
              />
              <input
                style={styles.input}
                placeholder="CONTACT (OPTIONAL)"
                value={form.contact}
                onChange={(e) => set('contact', e.target.value)}
              />
            </div>

            <input style={styles.input} placeholder="EMAIL" value={form.email} onChange={(e) => set('email', e.target.value)} />

            <div style={styles.row2}>
              <input
                style={styles.input}
                placeholder="OFFICE PHONE"
                value={form.office}
                onChange={(e) => set('office', formatPhone(e.target.value))}
              />
              <input
                style={styles.input}
                placeholder="MOBILE PHONE"
                value={form.mobile}
                onChange={(e) => set('mobile', formatPhone(e.target.value))}
              />
            </div>

            <div style={styles.rowAddr}>
              <AddressAutocomplete
                inputStyle={styles.input}
                placeholder="STREET"
                value={form.street}
                onChange={(v) => set('street', v)}
                onAddressSelect={({ street, city, state, zip }) => {
                  set('street', street);
                  set('city', city);
                  set('state', state.slice(0, 2));
                  set('zip', zip);
                }}
              />
              <input style={styles.input} placeholder="CITY" value={form.city} onChange={(e) => set('city', e.target.value)} />
              <input style={styles.input} placeholder="ST" maxLength={2} value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase())} />
              <input style={styles.input} placeholder="ZIP" maxLength={5} value={form.zip} onChange={(e) => set('zip', e.target.value)} />
            </div>

            <select style={styles.input} value={form.terms} onChange={(e) => set('terms', e.target.value)}>
              <option value="COD">TERMS: COD (DUE TODAY)</option>
              <option value="NET15">TERMS: NET 15</option>
              <option value="NET30">TERMS: NET 30</option>
            </select>

            <textarea style={{ ...styles.input, height: '90px' }} placeholder="NOTES" value={form.notes} onChange={(e) => set('notes', e.target.value)} />

            <div style={styles.btnRow}>
              <button className="jf-hover-blue" style={styles.saveBtn} onClick={submit}>SAVE CUSTOMER</button>
              <button className="jf-hover-blue" style={styles.cancelBtn} onClick={onClose}>CANCEL</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ===============================
   ESTIMATE ENTRY
================================== */
const EstimateEntry = ({
  estimate,
  setEstimate,
  customers,
  inventory,
  setInventory: _setInventory, // kept (do not remove prop)
  cannedJobs,
  counties: _counties,
  coupons: _coupons,
  onSave
}) => {
  const [custSearch, setCustSearch] = useState(estimate.company || estimate.customer_name || '');
  const [showCustResults, setShowCustResults] = useState(false);

  const [showPartsPicker, setShowPartsPicker] = useState(false);
  const [showJobsPicker, setShowJobsPicker] = useState(false);
  const [partSearch, setPartSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');
  const [savedNotes, setSavedNotes] = useState([]);
  const [savedNoteSearches, setSavedNoteSearches] = useState({});
  const [openSavedNoteRow, setOpenSavedNoteRow] = useState(null);
  const noteTextareaRefs = useRef(new Map());
  const pendingNoteFocusRef = useRef(null);

  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);

  // ✅ NEW: Fetch settings from database
  const [settingsData, setSettingsData] = useState({
    business: {},
    branding: {},
    tax: { states: [] },
    discounts: []
  });

  // ✅ neon-blue hover for ALL entry buttons
  const [hoverKey, setHoverKey] = useState('');

  // ✅ NEW: Fetch all settings on mount
  useEffect(() => {
    const fetchAllSettings = async () => {
      try {
        const API = getApiBase();

        const taxRes = await fetch(`${API}/api/settings/taxes`, {
          headers: { ...getTenantHeaders() }
        });
        const taxData = taxRes.ok ? await taxRes.json() : {};

        const discRes = await fetch(`${API}/api/settings/discounts`, {
          headers: { ...getTenantHeaders() }
        });
        const discData = discRes.ok ? await discRes.json() : [];

        const states = Array.isArray(taxData?.data?.states) ? taxData.data.states : [];
        const homeState = states.find(s => s.is_home_state) || states[0] || null;

        setSettingsData({
          business: {},
          branding: {},
          tax: { states },
          discounts: Array.isArray(discData) ? discData : []
        });

        if (
          Array.isArray(estimate.selected_local_tax_ids) &&
          (!estimate.state_abbr || estimate.state_abbr === '') &&
          homeState
        ) {
          const defCounty = (homeState.counties || []).find(c => c.is_default);
          applyEstimatePatch({
            sales_tax_rate: Number(homeState.state_sales_tax_rate || 0),
            state_abbr: homeState.state_abbr || '',
            state_name: homeState.state_name || '',
            county_sales_tax_rate: defCounty ? Number(defCounty.rate || 0) : 0,
            county_name: defCounty ? defCounty.name : '',
            selected_local_tax_ids: defCounty ? [Number(defCounty.id)] : [],
          });
        }
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    };

    fetchAllSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchSavedNotes = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/settings/saved-notes`, {
          headers: { ...getTenantHeaders() }
        });
        if (!response.ok) return;
        const data = await response.json().catch(() => []);
        setSavedNotes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load saved notes:', e);
      }
    };

    fetchSavedNotes();
  }, []);

  const styles = {
    card: { backgroundColor: '#fff', border: '8px solid black', padding: '18px' },
    label: { fontWeight: 900, display: 'block', marginBottom: '6px', fontSize: '12px' },
    neonLabel: { fontWeight: 900, display: 'block', marginBottom: '6px', fontSize: '12px', color: '#FFA500' },

    input: { padding: '12px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },

    descArea: {
      padding: '12px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      resize: 'none',
      overflow: 'hidden',
      lineHeight: '1.2',
      minHeight: '48px'
    },

    savedNoteBox: { position: 'relative', marginBottom: '8px' },
    savedNoteSearch: { padding: '10px', border: '3px solid black', fontWeight: 900, width: '100%', fontSize: '14px', boxSizing: 'border-box' },
    savedNoteDropdown: { border: '3px solid black', position: 'absolute', backgroundColor: 'white', zIndex: 110, width: '100%', maxHeight: '180px', overflowY: 'auto', boxSizing: 'border-box' },
    savedNoteOption: { padding: '10px', cursor: 'pointer', borderBottom: '2px solid black', fontWeight: 900 },

    dropdown: { border: '4px solid black', position: 'absolute', backgroundColor: 'white', zIndex: 100, width: '100%', maxHeight: '220px', overflowY: 'auto' },

    customerRow: { display: 'flex', gap: '10px', alignItems: 'stretch', flexWrap: 'wrap' },
    customerBox: { flex: 1, minWidth: '260px', position: 'relative' },
    customerBtn: { backgroundColor: 'black', color: '#FFA500', border: '4px solid #E8761D', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', textTransform: 'uppercase', minWidth: '220px' },

    actionRow: { display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' },
    actionBtnLabor: { backgroundColor: '#d9ecff', color: 'black', border: '5px solid black', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', fontSize: '16px' },
    actionBtnParts: { backgroundColor: '#d9fdd3', color: 'black', border: '5px solid black', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', fontSize: '16px' },
    actionBtnNote: { backgroundColor: '#fff1b8', color: 'black', border: '5px solid black', fontWeight: 900, cursor: 'pointer', padding: '12px 14px', fontSize: '16px' },

    pickWrap: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' },
    pickBox: { flex: 1, minWidth: '260px' },
    pickBtnParts: { width: '100%', padding: '14px', fontWeight: 900, border: '5px solid black', cursor: 'pointer', backgroundColor: '#d9fdd3', textTransform: 'uppercase' },
    pickBtnJobs: { width: '100%', padding: '14px', fontWeight: 900, border: '5px solid black', cursor: 'pointer', backgroundColor: '#d9ecff', textTransform: 'uppercase' },

    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px' },
    picker: { background: '#fff', border: '10px solid black', width: '100%', maxWidth: '620px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
    pickerHeader: { padding: '14px', borderBottom: '6px solid black', fontWeight: 900, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    pickerList: { overflowY: 'auto', padding: '10px' },
    pickerItem: { border: '4px solid black', padding: '12px', marginBottom: '10px', fontWeight: 900, cursor: 'pointer' },
    pickerClose: { background: 'black', color: '#FFA500', border: '4px solid #E8761D', padding: '10px 14px', fontWeight: 900, cursor: 'pointer' },

    removeBtn: { background: '#ff3b3b', color: 'black', border: '4px solid black', fontWeight: 900, cursor: 'pointer', padding: '0 14px', fontSize: '18px', height: '44px' },
    totalBox: { backgroundColor: 'black', color: '#FFA500', padding: '18px', border: '8px solid #E8761D', marginTop: '16px' },
    saveBtn: { backgroundColor: '#1B4332', color: '#FFA500', border: '5px solid #E8761D', padding: '18px', fontWeight: 900, fontSize: '20px', cursor: 'pointer' }
  };

  const hoverBlue = (base, key) => {
    if (hoverKey !== key) return base;
    return { ...base, backgroundColor: '#FFC107', color: 'black', borderColor: '#FFA500' };
  };

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    (estimate.items || []).forEach((item, idx) => {
      if (item?.type !== 'note') return;
      const key = item.id || String(idx);
      const el = noteTextareaRefs.current.get(key);
      if (!el) return;

      el.style.height = 'auto';
      const fullHeight = el.scrollHeight;
      el.style.height = `${Math.min(fullHeight, 320)}px`;
      el.style.overflowY = fullHeight > 320 ? 'auto' : 'hidden';
    });

    const pendingFocus = pendingNoteFocusRef.current;
    if (!pendingFocus) return;

    const el = noteTextareaRefs.current.get(pendingFocus.key);
    pendingNoteFocusRef.current = null;
    if (!el) return;

    el.focus({ preventScroll: true });
    window.scrollTo(pendingFocus.scrollX, pendingFocus.scrollY);
  }, [estimate.items]);

  // ✅ Customer search (supports both LS shape and DB shape)
  const customerResults = useMemo(() => {
    const q = (custSearch || '').trim().toLowerCase();
    if (!q) return [];

    return (customers || []).filter(c => {
      const company = String(c?.company || c?.name || '').toLowerCase();
      const contact = String(c?.contact || c?.contactPerson || '').toLowerCase();
      const email = String(c?.email || c?.billingEmail || '').toLowerCase();
      const phone = String(c?.phone || c?.mobile || c?.office || '').toLowerCase();
      return `${company} ${contact} ${email} ${phone}`.includes(q);
    });
  }, [custSearch, customers]);

  const applyEstimatePatch = (patch) => {
    const next = { ...estimate, ...patch };

    const items = Array.isArray(next.items) ? next.items : [];
    const normalizedItems = items.map((it) => {
      if (!it || it.type === 'note') return it;
      if (typeof it.taxable === 'boolean') return it;
      return { ...it, taxable: false };
    });

    if (normalizedItems !== items) next.items = normalizedItems;

    const { subtotal, total, stateTaxAmount, countyTaxAmount } = computeTotals(next);
    setEstimate({ ...next, subtotal, total, stateTaxAmount, countyTaxAmount });
  };

  const handleLocalTaxSelectionChange = (selectedLocalTaxes) => {
    const selectedRecords = Array.isArray(selectedLocalTaxes) ? selectedLocalTaxes : [];
    applyEstimatePatch({
      selected_local_tax_ids: selectedRecords.map(localTax => Number(localTax.id)),
      county_sales_tax_rate: selectedRecords.reduce(
        (sum, localTax) => sum + Number(localTax.rate || 0),
        0
      ),
      county_name: selectedRecords.map(localTax => localTax.name).filter(Boolean).join(', ')
    });
  };

  const handleStateChange = (
    stateAbbr,
    stateName,
    stateSalesTaxRate,
    defaultCountyRate,
    defaultCountyName
  ) => {
    const selectedState = (settingsData.tax.states || []).find(
      state => state.state_abbr === stateAbbr
    );
    const defaultCounty = (selectedState?.counties || []).find(county => county.is_default);

    applyEstimatePatch({
      state_abbr: stateAbbr,
      state_name: stateName,
      sales_tax_rate: stateSalesTaxRate,
      county_sales_tax_rate: defaultCountyRate,
      county_name: defaultCountyName,
      selected_local_tax_ids: defaultCounty ? [Number(defaultCounty.id)] : []
    });
  };

  const addLaborLine = () => {
    const items = [
      ...(estimate.items || []),
      { id: safeUUID(), type: 'labor', description: '', qty: 1, rate: '', taxable: false }
    ];
    applyEstimatePatch({ items });
  };

  const addPartLine = () => {
    const items = [
      ...(estimate.items || []),
      { id: safeUUID(), type: 'parts_custom', description: '', qty: 1, cost: '', markup: '', rate: '', taxable: false }
    ];
    applyEstimatePatch({ items });
  };

  const addNoteLine = () => {
    const items = [...(estimate.items || []), { id: safeUUID(), type: 'note', description: '' }];
    applyEstimatePatch({ items });
  };

  const removeItem = (idx) => {
    const items = (estimate.items || []).filter((_, i) => i !== idx);
    applyEstimatePatch({ items });
  };

  const updateItem = (idx, patch) => {
    const items = [...(estimate.items || [])];
    items[idx] = { ...items[idx], ...patch };

    if (items[idx].type === 'parts_custom') {
      if (items[idx].cost === '' || items[idx].cost == null) {
        items[idx].rate = '';
      } else {
        const cost = Number(items[idx].cost || 0);
        const markup = Number(items[idx].markup || 0);
        const retail = cost * (1 + markup / 100);
        items[idx].rate = Number.isFinite(retail) ? roundCurrency(retail) : Number(items[idx].rate || 0);
      }
    }

    applyEstimatePatch({ items });
  };

  const addItemFromInventory = (source) => {
    const name = getInvName(source);
    const extra = getInvDesc(source);
    const description = extra ? `${name} - ${extra}` : name;

    const item = {
      id: safeUUID(),
      type: 'parts_inventory',
      description,
      qty: 1,
      rate: getRetail(source),
      source_id: source?.id || null,
      taxable: false
    };

    // ✅ Estimates should NOT decrement inventory (only invoices do that on SAVE)
    const items = [...(estimate.items || []), item];
    applyEstimatePatch({ items });
  };

  const addItemFromJob = (job) => {
    const item = {
      id: safeUUID(),
      type: 'labor',
      description: job?.jobName || job?.name || 'Job',
      qty: 1,
      rate: Number(job?.price || job?.rate || 0),
      source_id: job?.id || null,
      taxable: false
    };
    const items = [...(estimate.items || []), item];
    applyEstimatePatch({ items });
  };

  // ✅ Quick Add Customer — DB truth guard:
  // - customers.name is NOT NULL
  // - If "company" (Customer Name / Company) is blank but contact exists, use contact as name
  const saveQuickCustomer = async (newCustomer) => {
    try {
      const API = getApiBase();

      const street = (newCustomer?.street || '').trim();
      const city = (newCustomer?.city || '').trim();
      const state = (newCustomer?.state || '').trim();
      const zip = (newCustomer?.zip || '').trim();
      const addressLine = [street, [city, state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

      const companyOrName = String(newCustomer?.company || '').trim();
      const contact = String(newCustomer?.contact || '').trim();
      const requiredName = companyOrName || contact; // ✅ fallback for residential

      const payload = {
        name: requiredName,
        contactPerson: contact,
        phone: digitsOnly(newCustomer?.office || newCustomer?.mobile || ''),
        office_phone: digitsOnly(newCustomer?.office || ''),
        mobile_phone: digitsOnly(newCustomer?.mobile || ''),
        email: String(newCustomer?.email || '').trim(),
        billingEmail: String(newCustomer?.email || '').trim(),
        street,
        city,
        state,
        zip,
        address: addressLine,
        terms: String(newCustomer?.terms || 'COD').trim(),
        notes: String(newCustomer?.notes || '').trim()
      };

      if (!payload.name) {
        alert('Please enter at least a CUSTOMER NAME/COMPANY or CONTACT.');
        return;
      }

      const res = await fetch(`${API}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || 'Customer create failed');
      }

      const data = await res.json();
      const created = data?.customer || data;
      const customerId = created?.id || created?.insertId || Date.now();

      // Apply to estimate (keep existing estimate schema untouched)
      const addr = buildAddressLine({ street, city, state, zip });

      applyEstimatePatch({
        customer_id: customerId,
        company: companyOrName || requiredName,
        customer_name: contact || '',
        email: payload.email || '',
        office_phone: String(newCustomer?.office || '').trim(),
        mobile_phone: String(newCustomer?.mobile || '').trim(),
        street,
        city,
        state,
        zip,
        address: addr
      });

      setCustSearch(companyOrName || contact || requiredName);
      setShowCustResults(false);
      setShowQuickAddCustomer(false);
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'CUSTOMER ADDED!' },
      }));
    } catch (e) {
      console.error('Quick Add Customer error:', e);
      alert(e.message || 'Failed to save customer to database.');
    }
  };

  return (
    <div style={styles.card}>
      {/* CUSTOMER + QUICK ADD */}
      <div style={{ marginBottom: '14px' }}>
        <label style={styles.label}>SELECT CUSTOMER</label>

        <div style={styles.customerRow}>
          <div style={styles.customerBox}>
            <input
              style={styles.input}
              value={custSearch}
              onChange={(e) => { setCustSearch(e.target.value); setShowCustResults(true); }}
              onFocus={() => setShowCustResults(true)}
              onBlur={() => setTimeout(() => setShowCustResults(false), 150)}
              placeholder="Start typing customer name..."
            />

            {showCustResults && custSearch && customerResults.length > 0 && (
              <div style={styles.dropdown}>
                {customerResults.map(c => (
                  <div
                    key={c.id}
                    style={{ padding: '10px', cursor: 'pointer', borderBottom: '2px solid black', fontWeight: 900 }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const addr = buildAddressLine(c);
                      applyEstimatePatch({
                        customer_id: c.id,
                        customer_name: c.contact || c.contactPerson || '',
                        company: c.company || c.name || '',
                        email: c.email || c.billingEmail || '',
                        office_phone: c.office || '',
                        mobile_phone: c.mobile || c.phone || '',
                        street: c.street || '',
                        city: c.city || '',
                        state: c.state || '',
                        zip: c.zip || '',
                        address: addr
                      });
                      setCustSearch((c.company || c.name || c.contact || c.contactPerson || '').trim());
                      setShowCustResults(false);
                    }}
                  >
                    {(c.company || c.name || '(No Customer Name/Company)')} | {(c.contact || c.contactPerson || '(No Contact)')}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            style={hoverBlue(styles.customerBtn, 'addCustomer')}
            onMouseEnter={() => setHoverKey('addCustomer')}
            onMouseLeave={() => setHoverKey('')}
            onClick={() => setShowQuickAddCustomer(true)}
          >
            + ADD CUSTOMER
          </button>
        </div>
      </div>

      {/* ADD BUTTONS */}
      <div style={styles.actionRow}>
        <button
          style={hoverBlue(styles.actionBtnLabor, 'addLabor')}
          onMouseEnter={() => setHoverKey('addLabor')}
          onMouseLeave={() => setHoverKey('')}
          onClick={addLaborLine}
        >
          + ADD LABOR LINE
        </button>

        <button
          style={hoverBlue(styles.actionBtnParts, 'addPart')}
          onMouseEnter={() => setHoverKey('addPart')}
          onMouseLeave={() => setHoverKey('')}
          onClick={addPartLine}
        >
          + ADD PART LINE
        </button>

        <button
          style={hoverBlue(styles.actionBtnNote, 'addNote')}
          onMouseEnter={() => setHoverKey('addNote')}
          onMouseLeave={() => setHoverKey('')}
          onClick={addNoteLine}
        >
          + ADD NOTE LINE
        </button>
      </div>

      {/* PICKERS */}
      <div style={styles.pickWrap}>
        <div style={styles.pickBox}>
          <label style={styles.label}>PART / ITEM (INVENTORY)</label>
          <button
            style={hoverBlue(styles.pickBtnParts, 'pickPart')}
            onMouseEnter={() => setHoverKey('pickPart')}
            onMouseLeave={() => setHoverKey('')}
            onClick={() => setShowPartsPicker(true)}
          >
            PICK A PART
          </button>
        </div>

        <div style={styles.pickBox}>
          <label style={styles.label}>CANNED JOB (LABOR)</label>
          <button
            style={hoverBlue(styles.pickBtnJobs, 'pickJob')}
            onMouseEnter={() => setHoverKey('pickJob')}
            onMouseLeave={() => setHoverKey('')}
            onClick={() => setShowJobsPicker(true)}
          >
            PICK A JOB
          </button>
        </div>
      </div>

      {/* LINE ITEMS */}
      <style>{`
        .jf-item { margin-bottom: 14px; }
        .jf-descRow { margin-bottom: 8px; }
        .jf-numRow { display:flex; gap:10px; align-items:center; flex-wrap: nowrap; }
        .jf-w-qty { width: 90px; }
        .jf-w-rate { width: 140px; }
        .jf-w-tax { width: 86px; }
        .jf-x { width: 54px; }
        .jf-customPartRow {
          display: grid;
          grid-template-columns: 100px minmax(110px, 1fr) minmax(110px, 1fr) minmax(110px, 1fr) 100px minmax(110px, 1fr) 64px;
          gap: 10px;
          align-items: end;
        }
        .jf-customPartField { min-width: 0; }
        .jf-customPartLabel { display: block; margin-bottom: 6px; font-size: 12px; font-weight: 900; }
        .jf-lineNumber::-webkit-outer-spin-button,
        .jf-lineNumber::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .jf-lineNumber[type="number"] { -moz-appearance: textfield; appearance: textfield; }
        .jf-standardItemRow {
          display: grid;
          grid-template-columns: 100px minmax(110px, 160px) 100px minmax(110px, 140px) 64px;
          gap: 10px;
          align-items: end;
        }

        @media (max-width: 520px) {
          .jf-numRow { flex-wrap: wrap; }
          .jf-w-qty { width: 100px; }
          .jf-w-rate { width: 140px; }
          .jf-customPartRow { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .jf-standardItemRow { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>

      {(estimate.items || []).map((item, idx) => {
        const isNote = item.type === 'note';
        const isCustomPart = item.type === 'parts_custom';
        const qty = Number(item.qty || 1);
        const rate = Number(item.rate || 0);
        const lineTotal = isNote ? 0 : qty * rate;
        const key = item.id || String(idx);
        const taxableChecked = item.taxable === true;

        if (isNote) {
          const savedNoteSearch = savedNoteSearches[key] || '';
          const matchingSavedNotes = savedNotes.filter(savedNote =>
            !savedNoteSearch || String(savedNote.name || '').toLowerCase().includes(savedNoteSearch.toLowerCase())
          );

          return (
            <div key={key} className="jf-item">
              {savedNotes.length > 0 && (
                <div style={styles.savedNoteBox}>
                  <input
                    type="text"
                    value={savedNoteSearch}
                    placeholder="SEARCH SAVED NOTES BY TITLE (OPTIONAL)..."
                    style={styles.savedNoteSearch}
                    onFocus={() => setOpenSavedNoteRow(key)}
                    onBlur={() => setTimeout(() => setOpenSavedNoteRow(null), 150)}
                    onChange={(e) => {
                      setSavedNoteSearches(prev => ({ ...prev, [key]: e.target.value }));
                      setOpenSavedNoteRow(key);
                    }}
                  />

                  {openSavedNoteRow === key && matchingSavedNotes.length > 0 && (
                    <div style={styles.savedNoteDropdown}>
                      {matchingSavedNotes.map(savedNote => (
                        <div
                          key={savedNote.id}
                          style={styles.savedNoteOption}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            pendingNoteFocusRef.current = {
                              key,
                              scrollX: window.scrollX,
                              scrollY: window.scrollY
                            };
                            updateItem(idx, { description: savedNote.note_text });
                            setSavedNoteSearches(prev => ({ ...prev, [key]: savedNote.name || '' }));
                            setOpenSavedNoteRow(null);
                          }}
                        >
                          {savedNote.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="jf-descRow">
                <textarea
                  ref={(el) => {
                    if (el) noteTextareaRefs.current.set(key, el);
                    else noteTextareaRefs.current.delete(key);
                  }}
                  style={styles.descArea}
                  value={item.description}
                  rows={1}
                  placeholder="NOTE (prints, does not affect totals)…"
                  onChange={(e) => updateItem(idx, { description: e.target.value })}
                />
              </div>
              <div className="jf-numRow">
                <button
                  style={hoverBlue(styles.removeBtn, `rm_${key}`)}
                  onMouseEnter={() => setHoverKey(`rm_${key}`)}
                  onMouseLeave={() => setHoverKey('')}
                  onClick={() => removeItem(idx)}
                >
                  X
                </button>
              </div>
            </div>
          );
        }

        return (
          <div key={key} className="jf-item">
            <div className="jf-descRow">
              <textarea
                style={styles.descArea}
                value={item.description}
                rows={1}
                placeholder={item.type === 'labor' ? 'Labor description…' : 'Part description…'}
                onInput={(e) => autoGrow(e.target)}
                onChange={(e) => updateItem(idx, { description: e.target.value })}
              />
            </div>

            {isCustomPart && (
              <div className="jf-customPartRow">
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">QTY</label>
                  <input className="jf-lineNumber" style={styles.input} type="number" value={item.qty} onChange={(e) => updateItem(idx, { qty: e.target.value })} onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">COST</label>
                  <input className="jf-lineNumber" style={styles.input} type="number" value={item.cost ?? ''} onChange={(e) => updateItem(idx, { cost: e.target.value })} placeholder="COST" onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">MARKUP %</label>
                  <input className="jf-lineNumber" style={styles.input} type="number" value={item.markup ?? ''} onChange={(e) => updateItem(idx, { markup: e.target.value })} placeholder="MARKUP %" onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">RETAIL</label>
                  <input className="jf-lineNumber" style={styles.input} type="number" value={item.rate} onChange={(e) => updateItem(idx, { rate: e.target.value })} placeholder="RETAIL" onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TAX</label>
                  <label style={{ ...styles.input, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={taxableChecked} onChange={(e) => updateItem(idx, { taxable: e.target.checked })} style={{ transform: 'scale(1.2)' }} />
                    TAX
                  </label>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TOTAL</label>
                  <div style={{ ...styles.input, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{money(lineTotal)}</div>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">ACTIONS</label>
                  <button className="jf-x" style={hoverBlue({ ...styles.removeBtn, width: '100%' }, `rm2_${key}`)} onMouseEnter={() => setHoverKey(`rm2_${key}`)} onMouseLeave={() => setHoverKey('')} onClick={() => removeItem(idx)}>X</button>
                </div>
              </div>
            )}

            {!isCustomPart && (
              <div className="jf-standardItemRow">
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">QTY</label>
                  <input className="jf-lineNumber" style={styles.input} type="number" value={item.qty} onChange={(e) => updateItem(idx, { qty: e.target.value })} onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">{item.type === 'labor' ? 'RATE' : 'RETAIL'}</label>
                  <input className="jf-lineNumber" style={styles.input} type="number" value={item.rate} onChange={(e) => updateItem(idx, { rate: e.target.value })} placeholder={item.type === 'labor' ? 'RATE' : 'RETAIL'} onWheel={e => e.target.blur()} />
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TAX</label>
                  <label style={{ ...styles.input, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={taxableChecked} onChange={(e) => updateItem(idx, { taxable: e.target.checked })} style={{ transform: 'scale(1.2)' }} />
                    TAX
                  </label>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">TOTAL</label>
                  <div style={{ ...styles.input, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{money(lineTotal)}</div>
                </div>
                <div className="jf-customPartField">
                  <label className="jf-customPartLabel">ACTIONS</label>
                  <button className="jf-x" style={hoverBlue({ ...styles.removeBtn, width: '100%' }, `rm2_${key}`)} onMouseEnter={() => setHoverKey(`rm2_${key}`)} onMouseLeave={() => setHoverKey('')} onClick={() => removeItem(idx)}>X</button>
                </div>
              </div>
            )}

            <div style={{ fontWeight: 900, marginTop: '6px', fontSize: '14px' }}>
              LINE TOTAL: {money(lineTotal)}
            </div>
          </div>
        );
      })}

      {/* TOTALS */}
      <div style={styles.totalBox}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', marginBottom: '10px' }}>
          <div style={{ ...styles.input, background: '#111', color: '#FFA500' }}>
            SUBTOTAL: {money(estimate.subtotal)}
          </div>

          <select
            style={styles.input}
            value={estimate.discount_selection || '0'}
            onChange={e => applyEstimatePatch({ discount: e.target.value, discount_selection: e.target.value })}
          >
            <option value="0">COUPON / DISCOUNT</option>
            {[...(settingsData.discounts || [])]
              .sort((a, b) => compareVisibleNames(a.name, b.name))
              .map(c => (
              <option key={c.id} value={`${c.type || "fixed"}:${Number(c.value || 0)}:${c.id}`}>
                {c.name} ({c.type === "percentage" ? `${c.value}%` : money(c.value)} OFF)
              </option>
              ))}
          </select>

          {(() => {
            const discountStr = String(estimate.discount_selection || estimate.discount || '0');
            if (!discountStr.includes(':')) return null;
            const parts = discountStr.split(':');
            const discType = parts[0];
            const discId = parts[2];
            const matched = (settingsData.discounts || []).find(d => String(d.id) === String(discId));
            if (!matched) return null;
            return (
              <>
                <div style={{ ...styles.input, background: '#111', color: '#FFA500', fontSize: '13px' }}>
                  DISCOUNT: {matched.name || matched.description || ''}
                </div>
                {discType === 'variable' && (
                  <div>
                    <label style={styles.neonLabel}>DISCOUNT AMOUNT ($)</label>
                    <input
                      style={styles.input}
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={(() => {
                        const dStr = String(estimate.discount || '0');
                        if (dStr.includes(':')) {
                          const v = dStr.split(':')[1] || '';
                          return v === '0' || v === '' ? '' : v;
                        }
                        const selStr = String(estimate.discount_selection || '0');
                        if (selStr.includes(':')) {
                          const v = selStr.split(':')[1] || '';
                          return v === '0' || v === '' ? '' : v;
                        }
                        return '';
                      })()}
                      onChange={e => {
                        const amt = e.target.value || '0';
                        applyEstimatePatch({ discount: `variable:${amt}:${discId}` });
                      }}
                      onWheel={e => e.target.blur()}
                    />
                  </div>
                )}
              </>
            );
          })()}

          <InvoiceTaxPanel
            states={settingsData.tax.states}
            selectedStateAbbr={estimate.state_abbr || ''}
            salesTaxRate={estimate.sales_tax_rate}
            selectedCountyName={estimate.county_name || ''}
            selectedLocalTaxIds={estimate.selected_local_tax_ids}
            disabled={false}
            onSalesTaxChange={val => applyEstimatePatch({ sales_tax_rate: val })}
            onCountyChange={(rate, countyName) => applyEstimatePatch({ county_sales_tax_rate: rate, county_name: countyName })}
            onLocalTaxSelectionChange={handleLocalTaxSelectionChange}
            onStateChange={handleStateChange}
            inputStyle={styles.input}
            neonLabelStyle={styles.neonLabel}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h2 style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 10px', margin: 0, maxWidth: '100%' }}>
            <span style={{ fontSize: 'clamp(18px, 6vw, 44px)' }}>TOTAL:</span>
            <span style={{ fontSize: 'clamp(16px, 6vw, 44px)', whiteSpace: 'nowrap' }}>{money(estimate.total)}</span>
          </h2>

          <button
            style={hoverBlue(styles.saveBtn, 'save')}
            onMouseEnter={() => setHoverKey('save')}
            onMouseLeave={() => setHoverKey('')}
            onClick={() => onSave(estimate)}
          >
            SAVE ESTIMATE
          </button>
        </div>
      </div>

      {/* PARTS PICKER */}
      {showPartsPicker && (
        <div style={styles.overlay} onClick={() => { setShowPartsPicker(false); setPartSearch(''); }}>
          <div style={styles.picker} onClick={(e) => e.stopPropagation()}>
            <div style={styles.pickerHeader}>
              <div>PICK A PART</div>
              <button
                style={hoverBlue(styles.pickerClose, 'closeParts')}
                onMouseEnter={() => setHoverKey('closeParts')}
                onMouseLeave={() => setHoverKey('')}
                onClick={() => { setShowPartsPicker(false); setPartSearch(''); }}
              >
                CLOSE
              </button>
            </div>

            <input
              type="text"
              placeholder="Search parts..."
              value={partSearch}
              onChange={(e) => setPartSearch(e.target.value)}
              style={{ margin: '10px', padding: '8px', border: '3px solid black', fontWeight: 700, fontSize: '14px', width: 'calc(100% - 20px)', boxSizing: 'border-box' }}
              autoFocus
            />

            <div style={styles.pickerList}>
              {(inventory || []).filter(i => !partSearch || getInvName(i).toLowerCase().includes(partSearch.toLowerCase())).map(i => {
                const qty = getInvQty(i);
                const out = hasInvQtyField(i) && qty !== null && qty <= 0;
                const retail = getRetail(i);
                const name = getInvName(i);

                return (
                  <div
                    key={i.id}
                    style={{ ...styles.pickerItem, opacity: out ? 0.45 : 1, background: out ? '#f2f2f2' : '#fff' }}
                    onClick={() => {
                      if (out) return;
                      addItemFromInventory(i);
                      setShowPartsPicker(false);
                      setPartSearch('');
                    }}
                  >
                    {name} — {money(retail)}
                    {hasInvQtyField(i) && qty !== null ? ` (QTY: ${qty})` : ''}
                    {out ? ' — OUT OF STOCK' : ''}
                  </div>
                );
              })}

              {(inventory || []).filter(i => !partSearch || getInvName(i).toLowerCase().includes(partSearch.toLowerCase())).length === 0 && (
                <div style={{ fontWeight: 900, padding: '12px' }}>{(inventory || []).length === 0 ? 'No inventory items yet.' : 'No parts match your search.'}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* JOBS PICKER */}
      {showJobsPicker && (
        <div style={styles.overlay} onClick={() => { setShowJobsPicker(false); setJobSearch(''); }}>
          <div style={styles.picker} onClick={(e) => e.stopPropagation()}>
            <div style={styles.pickerHeader}>
              <div>PICK A JOB</div>
              <button
                style={hoverBlue(styles.pickerClose, 'closeJobs')}
                onMouseEnter={() => setHoverKey('closeJobs')}
                onMouseLeave={() => setHoverKey('')}
                onClick={() => { setShowJobsPicker(false); setJobSearch(''); }}
              >
                CLOSE
              </button>
            </div>

            <input
              type="text"
              placeholder="Search jobs..."
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
              style={{ margin: '10px', padding: '8px', border: '3px solid black', fontWeight: 700, fontSize: '14px', width: 'calc(100% - 20px)', boxSizing: 'border-box' }}
              autoFocus
            />

            <div style={styles.pickerList}>
              {(cannedJobs || []).filter(j => !jobSearch || (j.jobName || j.name || '').toLowerCase().includes(jobSearch.toLowerCase())).map(j => (
                <div
                  key={j.id}
                  style={styles.pickerItem}
                  onClick={() => {
                    addItemFromJob(j);
                    setShowJobsPicker(false);
                    setJobSearch('');
                  }}
                >
                  {(j.jobName || j.name || 'Job')} — {money(Number(j.price || j.rate || 0))}
                </div>
              ))}

              {(cannedJobs || []).filter(j => !jobSearch || (j.jobName || j.name || '').toLowerCase().includes(jobSearch.toLowerCase())).length === 0 && (
                <div style={{ fontWeight: 900, padding: '12px' }}>{(cannedJobs || []).length === 0 ? 'No canned jobs yet.' : 'No jobs match your search.'}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QUICK ADD CUSTOMER MODAL */}
      {showQuickAddCustomer && (
        <QuickAddCustomerModal
          onClose={() => setShowQuickAddCustomer(false)}
          onSave={saveQuickCustomer}
        />
      )}
    </div>
  );
};

export default EstimateEntry;
